import { INSURANCE_AMOUNT_BY_COUNTRY } from '~/constants/insurance'
import { RV_MANUFACTURERS_OTHER } from '~/constants/manufacturers'
import { WEEKDAYS } from '~/constants/calendars'
import {
  COUNTRY_CURRENCIES,
  COUNTRY_SHORTS,
  TODAY_MAXIMUM_BOOKABLE_HOUR,
} from '~/constants'

import type {
  DaysOfWeek,
  Nullable,
  RvExpandedDetails,
} from '~/types'

/**
 * Returns a collection of basic RV details, advanced properties, and helpers.
 *
 * @param rv The RV to get the details for.
 * @returns The RV details.
 */
export function getRvDetails(rv?: Nullable<RvExpandedDetails>) {
  // Base properties.
  const rvAliasName = rv?.AliasName ?? null
  const rvAllowCrossBorderTravel = rv?.AllowCrossBorderTravel ?? false
  const rvAllowFestivalEventsParticipation = rv?.AllowFestivalEventsParticipation ?? null
  const rvAllowOnlyExperiencedSimilarRV = rv?.AllowOnlyExperiencedSimilarRV ?? false
  const rvAllowSmoking = rv?.AllowSmoking ?? false
  const rvAverageRating = rv?.AverageRating ?? 0
  const rvBedBunkDouble = rv?.BedBunkDouble ?? null
  const rvBedBunkTwin = rv?.BedBunkTwin ?? null
  const rvBedDinette = rv?.BedDinette ?? null
  const rvBedKing = rv?.BedKing ?? null
  const rvBedQueen = rv?.BedQueen ?? null
  const rvBedSofa = rv?.BedSofa ?? null
  const rvBookingSeasonEnd = rv?.BookingSeasonEnd ?? null
  const rvBookingSeasonStart = rv?.BookingSeasonStart ?? null
  const rvCalendars = rv?.Calendars ?? null
  const rvChargePerHourOver = rv?.ChargePerHourOver ?? null
  const rvCheckInAvailabilityDays = rv?.CheckInAvailabilityDays ?? []
  const rvCheckInEndTime = rv?.CheckInEndTime ?? null
  const rvCheckInStartTime = rv?.CheckInStartTime ?? null
  const rvCheckoutEndTime = rv?.CheckoutEndTime ?? null
  const rvCheckoutStartTime = rv?.CheckoutStartTime ?? null
  const rvCleaningFee = rv?.CleaningFee ?? 0
  const rvDailyKMAllowed = rv?.DailyKMAllowed ?? null
  const rvDefaultPrice = rv?.DefaultPrice ?? 0
  const rvDeliveryCentsPerDistanceUnit = rv?.DeliveryCentsPerDistanceUnit ?? null
  const rvDeliveryInstructions = rv?.DeliveryInstructions ?? null
  const rvDeliveryMaxDistance = rv?.DeliveryMaxDistance ?? null
  const rvDeliveryMinimumCostCents = rv?.DeliveryMinimumCostCents ?? null
  const rvDiscountedAverageNightlyPrice = rv?.DiscountedAverageNightlyPrice ?? 0
  const rvFreeHoursPerDay = rv?.FreeHoursPerDay ?? null
  const rvFuelType = rv?.FuelType ?? null
  const rvGuests = rv?.Guests ?? 0
  const rvHitchSize = rv?.HitchSize ?? null
  const rvHitchWeight = rv?.HitchWeight ?? null
  const rvId = rv?.Id ?? ''
  const rvInsideLeadDays = rv?.InsideLeadDays ?? 0
  const rvInstabookOwnerOptedIn = rv?.InstabookOwnerOptedIn ?? false
  const rvInteriorFeatures = rv?.InteriorFeatures ?? []
  const rvIsActivated = rv?.IsActivated ?? false
  const rvIsChargeMileage = rv?.IsChargeMileage ?? null
  const rvIsDeliveryOnly = rv?.IsDeliveryOnly ?? false
  const rvIsFeatured = rv?.IsFeatured ?? false
  const rvIsGenerator = rv?.IsGenerator ?? null
  const rvIsInstantBook = rv?.IsInstantBook ?? false
  const rvIsInsured = rv?.IsInsured ?? rv?.Insured ?? false
  const rvIsPublished = rv?.IsPublish ?? false
  const rvIsSuperHostActive = rv?.IsSuperHostActive ?? false
  const rvLatitude = rv?.Latitude ?? null
  const rvLength = rv?.Length ?? null
  const rvListAddOns = rv?.ListAddOns ?? null
  const rvLocation = rv?.Location ?? null
  const rvLongitude = rv?.Longitude ?? null
  const rvMake = rv?.Make ?? null
  const rvMidweekDiscount = rv?.MidweekDiscount ?? 0
  const rvMinimumRentalDay = rv?.MinimumRentalDay ?? 0
  const rvMinimumTripLength = rv?.MinimumTripLength ?? 0
  const rvModel = rv?.Model ?? null
  const rvMonthlyDiscount = rv?.MonthlyDiscount ?? 0
  const rvNumberOfReview = rv?.NumberOfReview ?? 0
  const rvOriginalPrice = rv?.OriginalPrice ?? 0
  const rvOutsideLeadDays = rv?.OutsideLeadDays ?? 0
  const rvOwnerAbout = rv?.OwnerAbout ?? null
  const rvOwnerAvatar = rv?.OwnerAvatar ?? null
  const rvOwnerCreatedAt = rv?.OwnerCreatedAt ?? null
  const rvOwnerFirstName = rv?.OwnerFirstName ?? null
  const rvOwnerId = rv?.OwnerId ?? 0
  const rvOwnerLastName = rv?.OwnerLastName ?? null
  const rvOwnerScore = rv?.OwnerScore ?? null
  const rvOwnerVerified = rv?.OwnerVerified ?? false
  const rvParkAmenities = rv?.ParkAmenities ?? null
  const rvPhotos = rv?.Photos ?? null
  const rvPreBookingMessage = rv?.PreBookingMessage ?? null
  const rvRentalType = rv?.RentalType
  const rvRenterCategoryRatings = rv?.RenterCategoryRatings ?? null
  const rvRenterRating = rv?.RenterRating ?? 0
  const rvReviews = rv?.Reviews ?? null
  const rvRVBrandOther = rv?.RVBrandOther ?? null
  const rvRVDescription = rv?.RVDescription ?? null
  const rvRVName = rv?.RVName ?? null
  const rvRVNumber = rv?.RVNumber ?? 0
  const rvRVSellingDate = rv?.RVSellingDate ?? null
  const rvRVType = rv?.RVType ?? null
  const rvSeatbeltThreePoint = rv?.SeatbeltThreePoint ?? null
  const rvSeatbeltTwoPoint = rv?.SeatbeltTwoPoint ?? null
  const rvSecurityDeposit = rv?.SecurityDeposit ?? null
  const rvSlideouts = rv?.Slideouts ?? null
  const rvSurchargePerExtraKM = rv?.SurchargePerExtraKM ?? null
  const rvTimezone = rv?.Timezone ?? null
  const rvTotalDiscount = rv?.TotalDiscount ?? null
  const rvTour3DUrl = rv?.Tour3DUrl ?? null
  const rvWeeklyDiscount = rv?.WeeklyDiscount ?? 0
  const rvWeight = rv?.Weight ?? null
  const rvYear = rv?.Year ?? 0

  // Advanced `has` checks.
  const rvHasAccommodations = (rvBedBunkDouble ?? 0) + (rvBedBunkTwin ?? 0) + (rvBedDinette ?? 0) + (rvBedKing ?? 0) + (rvBedQueen ?? 0) + (rvBedSofa ?? 0) > 0
  const rvHasAddons = Boolean(rvListAddOns?.length)
  const rvHasAmenity = (amenity: string) => getRvAmenity(rvInteriorFeatures, amenity)
  const rvHasDelivery = Boolean(rvDeliveryMaxDistance && rvDeliveryCentsPerDistanceUnit && rvDeliveryMinimumCostCents)
  const rvHasRangeDiscounts = rvWeeklyDiscount > 0 || rvMonthlyDiscount > 0 || rvMidweekDiscount > 0

  // Basic `is` checks.
  const rvIsChargeGeneratorEnabled = rvFreeHoursPerDay !== null && rvChargePerHourOver !== null
  const rvIsChargeMileageEnabled = rvDailyKMAllowed !== null && rvSurchargePerExtraKM !== null && rvDailyKMAllowed >= 0 && rvSurchargePerExtraKM !== 0
  const rvIsFromCanada = rvLocation?.Country === COUNTRY_SHORTS.CA // TODO: replace constant with backend type
  const rvIsFromUs = rvLocation?.Country === COUNTRY_SHORTS.US // TODO: replace constant with backend type
  const rvIsMotorized = rvRentalType === 'MotorHome'
  const rvIsStatic = rvRentalType === 'RVCottage'
  const rvIsTowable = rvRentalType === 'TravelTrailer'

  // Advanced properties.
  const rvActiveAddons = rvListAddOns?.filter((addon) => addon?.Status === 'Active') ?? []
  const rvAllowPets = Boolean(rvInteriorFeatures.includes('Petfriendly'))
  const rvCategory = getRvCategory(rvRVType)
  const rvCity = rvLocation?.City ?? rv?.City ?? null
  const rvComputedTimezone = rvTimezone ?? rvLocation?.Timezone ?? 'America/New_York'
  const rvCountry = rvLocation?.Country ? (COUNTRY_SHORTS[rvLocation.Country as keyof typeof COUNTRY_SHORTS]) : rv?.Country ? (COUNTRY_SHORTS[rv.Country as keyof typeof COUNTRY_SHORTS]) : undefined // TODO: replace constant with backend type
  const rvCurrency = (rvIsFromCanada ? COUNTRY_CURRENCIES.CA : COUNTRY_CURRENCIES.US) // TODO: replace constant with backend type
  const rvDeliveryMaxDistanceInKm = rvIsFromUs && rvDeliveryMaxDistance ? miToKm(rvDeliveryMaxDistance) : rvDeliveryMaxDistance ?? 0
  const rvFormattedDescription = rvRVDescription ? rvRVDescription.replace(/"/g, '\\"') : ''
  const rvFormattedLocation = (rvLocation?.City && rvLocation?.State && rvLocation?.Country) ? `${rvLocation?.City}, ${rvLocation?.State}, ${lookupIsoCodeToCountry((rvLocation?.Country as 'CA' | 'US'))}` : ''
  const rvFormattedName = rvRVName ? rvRVName.replace(/"/g, '\\"') : ''
  const rvFullModelLabel = rvIsStatic ? `${rvRVName} (${rvYear})` : rvYear && rvModel ? `${rvYear} ${rvMake === RV_MANUFACTURERS_OTHER ? rvRVBrandOther : rvMake} ${rvModel}` : '' // TODO: replace constant with backend type
  const rvLiabilityAmount = rvIsFromUs ? INSURANCE_AMOUNT_BY_COUNTRY.US : INSURANCE_AMOUNT_BY_COUNTRY.CA
  const rvLocalizedRentalType = `rvType.${rvRentalType}`
  const rvLocalizedRVType = rvRVType ? `rvType.${rvRVType}` : 'rvType.rvCottage'
  const rvMainPhotoPath = rvPhotos?.[0]?.Path ?? null
  const rvMaxBookableDate = hasValue(rv?.OutsideLeadDays) ? datetime().tz(rvComputedTimezone).startOf('day').add(rvOutsideLeadDays, 'days') : undefined
  const rvMeasurementUnit = countryCodeToDistanceUnit(rvCountry)
  const rvMeasurementUnitShort = countryCodeToDistanceUnitShort(rvCountry)
  const rvMidweekPrice = rvDefaultPrice - (rvDefaultPrice / 100 * rvMidweekDiscount)

  /**
   * Min bookable date, in the RV's local timezone.
   *
   * Mirrors the back end's `LeadTimeHelper.GetLeadTimeBounds`: the earliest bookable date is
   * `today + InsideLeadDays`, plus one more day once the RV's local time reaches the same-day
   * cutoff hour. The bump applies at every `InsideLeadDays` value, not just 0 — otherwise the
   * calendar offers dates that `get-booking-price` rejects with `BOOKING_OVERLAPS_DATES`.
   */
  const rvLocalNow = datetime().tz(rvComputedTimezone)
  const rvMinBookableDate = hasValue(rv?.InsideLeadDays)
    ? rvLocalNow.startOf('day').add(rvInsideLeadDays + (rvLocalNow.hour() >= TODAY_MAXIMUM_BOOKABLE_HOUR ? 1 : 0), 'days')
    : undefined
  const rvMonthlyPrice = rvDefaultPrice - (rvDefaultPrice / 100 * rvMonthlyDiscount)
  const rvState = rvLocation?.State ?? rv?.State ?? null
  const rvWeeklyPrice = rvDefaultPrice - (rvDefaultPrice / 100 * rvWeeklyDiscount)

  const rvUnavailableDays = rvCheckInAvailabilityDays?.length
    ? Object.values(WEEKDAYS)
      .filter((weekday) => !rvCheckInAvailabilityDays?.includes(weekday))
      .map((weekday) => Object.values(WEEKDAYS).indexOf(weekday) + 1) as DaysOfWeek[]
    : []

  return {
    rvAliasName,
    rvAllowCrossBorderTravel,
    rvAllowFestivalEventsParticipation,
    rvAllowOnlyExperiencedSimilarRV,
    rvAllowSmoking,
    rvAverageRating,
    rvBedBunkDouble,
    rvBedBunkTwin,
    rvBedDinette,
    rvBedKing,
    rvBedQueen,
    rvBedSofa,
    rvBookingSeasonEnd,
    rvBookingSeasonStart,
    rvCalendars,
    rvChargePerHourOver,
    rvCheckInAvailabilityDays,
    rvCheckInEndTime,
    rvCheckInStartTime,
    rvCheckoutEndTime,
    rvCheckoutStartTime,
    rvCleaningFee,
    rvDailyKMAllowed,
    rvDefaultPrice,
    rvDeliveryCentsPerDistanceUnit,
    rvDeliveryInstructions,
    rvDeliveryMaxDistance,
    rvDeliveryMinimumCostCents,
    rvDiscountedAverageNightlyPrice,
    rvFreeHoursPerDay,
    rvFuelType,
    rvGuests,
    rvHitchSize,
    rvHitchWeight,
    rvId,
    rvInsideLeadDays,
    rvInstabookOwnerOptedIn,
    rvInteriorFeatures,
    rvIsActivated,
    rvIsChargeMileage,
    rvIsDeliveryOnly,
    rvIsFeatured,
    rvIsGenerator,
    rvIsInstantBook,
    rvIsInsured,
    rvIsPublished,
    rvIsSuperHostActive,
    rvLatitude,
    rvLongitude,
    rvLength,
    rvListAddOns,
    rvLocation,
    rvMake,
    rvMidweekDiscount,
    rvMinimumRentalDay,
    rvMinimumTripLength,
    rvModel,
    rvMonthlyDiscount,
    rvNumberOfReview,
    rvOriginalPrice,
    rvOutsideLeadDays,
    rvOwnerAbout,
    rvOwnerAvatar,
    rvOwnerCreatedAt,
    rvOwnerFirstName,
    rvOwnerId,
    rvOwnerLastName,
    rvOwnerScore,
    rvOwnerVerified,
    rvParkAmenities,
    rvPhotos,
    rvPreBookingMessage,
    rvRentalType,
    rvRenterCategoryRatings,
    rvRenterRating,
    rvReviews,
    rvRVBrandOther,
    rvRVDescription,
    rvRVName,
    rvRVNumber,
    rvRVSellingDate,
    rvRVType,
    rvSeatbeltThreePoint,
    rvSeatbeltTwoPoint,
    rvSecurityDeposit,
    rvSlideouts,
    rvSurchargePerExtraKM,
    rvTimezone,
    rvTotalDiscount,
    rvTour3DUrl,
    rvWeeklyDiscount,
    rvWeight,
    rvYear,

    rvHasAccommodations,
    rvHasAddons,
    rvHasAmenity,
    rvHasDelivery,
    rvHasRangeDiscounts,

    rvIsChargeGeneratorEnabled,
    rvIsChargeMileageEnabled,
    rvIsFromCanada,
    rvIsFromUs,
    rvIsMotorized,
    rvIsStatic,
    rvIsTowable,

    rvActiveAddons,
    rvAllowPets,
    rvCategory,
    rvCity,
    rvComputedTimezone,
    rvCountry,
    rvCurrency,
    rvDeliveryMaxDistanceInKm,
    rvFormattedDescription,
    rvFormattedLocation,
    rvFormattedName,
    rvFullModelLabel,
    rvLiabilityAmount,
    rvLocalizedRentalType,
    rvLocalizedRVType,
    rvMainPhotoPath,
    rvMaxBookableDate,
    rvMeasurementUnit,
    rvMeasurementUnitShort,
    rvMidweekPrice,
    rvMinBookableDate,
    rvMonthlyPrice,
    rvState,
    rvWeeklyPrice,

    // TODO: move to the composable.
    rvUnavailableDays,
  }
}
